// lib/source-actions.ts
import { z } from 'zod';
import type { KernelState, SourceActionMetadata } from '#types/types';
import { selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';

const identifier = z.string().min(1).max(128).regex(/^[a-zA-Z0-9_:-]+$/);
const count = z.number().int().min(0).max(1_000_000);
const tabs = ['FOUNDATION', 'UNITS', 'THREE_D', 'MAPPER', 'OVERVIEW', 'LEADS', 'DEALS', 'CRITICAL_QUESTIONS', 'PROPERTIES', 'PORTFOLIO_FIT_CAMPAIGNS', 'PORTFOLIO_FITS', 'LISTING', 'INTEGRITY', 'FINANCIALS', 'BRANDS', 'LEGAL_ENTITIES', 'CONTACTS', 'FACTS', 'ECONOMICS', 'CONTACT', 'DRAFTING', 'DESIGNS', 'UNIT', 'SUMMARY', 'SCORING', 'EXPANSION', 'STORES', 'REVENUE'] as const;
const contextKeys = ['entityType', 'entityId', 'chatSessionId', 'campaignId', 'tab'];
const fields = {
  navigation: [], tab: [], search: ['hasQuery'], filter: ['filterCount'], chat: ['queued'],
  shortlist: ['status', 'count'], contacts: ['count', 'acceptedCount'], export: ['count', 'format'],
};
const phases = {
  navigation: ['observed'], tab: ['observed'], search: ['observed'], filter: ['observed'],
  chat: ['requested', 'client_failed', 'client_cancelled'],
  shortlist: ['requested', 'client_returned', 'client_failed', 'client_cancelled'],
  contacts: ['requested', 'client_returned', 'client_failed', 'client_cancelled'],
  export: ['requested', 'download_started', 'client_failed'],
};

export const sourceActionSchema = z.object({
  operationId: z.string().uuid(),
  action: z.enum(['navigation', 'tab', 'search', 'filter', 'chat', 'shortlist', 'contacts', 'export']),
  origin: z.enum(['human', 'agent', 'unknown', 'observed']),
  phase: z.enum(['observed', 'requested', 'client_returned', 'client_failed', 'client_cancelled', 'download_started']),
  data: z.object({
    entityType: z.enum(['Portfolio', 'Property', 'Unit', 'Floor', 'Brand', 'Contact', 'Lead', 'PortfolioFit', 'Campaign', 'SourcingCampaign', 'PortfolioFitCampaign', 'Deal', 'LegalEntity', 'MarketSignal']).optional(),
    entityId: identifier.optional(), chatSessionId: identifier.optional(), campaignId: identifier.optional(),
    tab: z.enum(tabs).optional(), count: count.optional(), acceptedCount: count.optional(),
    hasQuery: z.boolean().optional(), filterCount: count.optional(),
    status: z.enum(['INTERESTING', 'DISMISSED']).optional(), queued: z.boolean().optional(), format: z.literal('csv').optional(),
  }).strict(),
}).strict().superRefine((event, ctx) => {
  const allowed = [...contextKeys, ...fields[event.action]];
  if (!phases[event.action].includes(event.phase) || Object.keys(event.data).some((key) => !allowed.includes(key))) {
    ctx.addIssue({ code: 'custom', message: 'Unsupported Source action metadata or phase.' });
  }
  const observed = event.action === 'navigation' || event.action === 'tab';
  if (observed ? event.origin !== 'observed' : event.origin === 'observed' || (['search', 'filter', 'chat', 'export'].includes(event.action) && event.origin !== 'human')) {
    ctx.addIssue({ code: 'custom', message: 'Unsupported Source action origin.' });
  }
  const required = fields[event.action].filter((field) => field !== 'acceptedCount' && !(event.action === 'contacts' && field === 'count'));
  if (required.some((field) => !Object.prototype.hasOwnProperty.call(event.data, field))) {
    ctx.addIssue({ code: 'custom', message: 'Missing Source action metadata.' });
  }
});

export function sourceActionContext(state: KernelState): SourceActionMetadata {
  const subject = selectActiveSubjectFromInventoryState(state);
  const runtime = subject ? state.entityKernelState[`${subject.entityType}:${subject.entityId}`]?.state as { activeTab?: unknown; runtime?: { activeTab?: unknown } } | undefined : undefined;
  const tab = runtime?.activeTab ?? runtime?.runtime?.activeTab;
  return {
    ...(state.activeChatSessionId ? { chatSessionId: state.activeChatSessionId } : {}),
    ...(subject ? { entityType: subject.entityType, entityId: subject.entityId } : {}),
    ...(subject?.campaignId ? { campaignId: subject.campaignId } : {}),
    ...(typeof tab === 'string' && (tabs as readonly string[]).includes(tab) ? { tab } : {}),
  };
}

export const sourceActionFailure = (error: unknown): 'client_failed' | 'client_cancelled' =>
  error instanceof Error && error.name === 'AbortError' ? 'client_cancelled' : 'client_failed';
